import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Ship, Plane, MapPin } from "lucide-react";
import { MaskText } from "../Reveal";
import { useLang } from "../../i18n/LanguageContext";
import { ASSETS, waLink } from "../../lib/site";

const CHINA = { left: "51%", top: "29%" };
const INDO = { left: "50%", top: "72%" };

const TradeMap = ({ compact = false }) => (
  <div data-testid="hero-trade-map" className={`relative h-full w-full ${compact ? "opacity-95" : ""}`}>
    <img src={ASSETS.worldMap} alt="" className="absolute inset-0 h-full w-full object-contain opacity-70 mix-blend-multiply" />

    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
      {/* Sea route (ship) bows right */}
      <path className="route-dash" d="M51 29 Q70 50 50 72" fill="none" stroke="#0D47A1" strokeWidth="0.9" />
      {/* Air route (plane) bows left */}
      <path className="route-dash" style={{ animationDuration: "0.6s" }} d="M51 29 Q31 50 50 72" fill="none" stroke="#E53935" strokeWidth="0.9" />
    </svg>

    {/* Pins */}
    <div data-testid="hero-pin-china" className="absolute -translate-x-1/2 -translate-y-full flex flex-col items-center" style={{ left: CHINA.left, top: CHINA.top }}>
      <span className="mb-0.5 text-[11px] font-bold tracking-wide text-sinovar-navy/70">CHINA</span>
      <MapPin className="h-6 w-6 text-sinovar-red" fill="#E53935" strokeWidth={1.2} />
    </div>
    <div data-testid="hero-pin-indonesia" className="absolute -translate-x-1/2 -translate-y-6 flex flex-col items-center" style={{ left: INDO.left, top: INDO.top }}>
      <MapPin className="h-6 w-6 text-sinovar-red" fill="#E53935" strokeWidth={1.2} />
      <span className="mt-0.5 text-[11px] font-bold tracking-wide text-sinovar-red">INDONESIA</span>
    </div>

    {/* Animated ship (sea route on the right, slower, arrives later) */}
    <motion.div
      data-testid="hero-marker-ship"
      className="absolute z-10 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-sinovar-blue shadow-lg shadow-sinovar-blue/30"
      animate={{ left: ["51%", "70%", "50%"], top: ["29%", "50%", "72%"] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", times: [0, 0.5, 1], repeatDelay: 0.6 }}
    >
      <Ship className="h-4 w-4 text-white" strokeWidth={2} />
    </motion.div>

    {/* Animated plane (air route on the left, faster, arrives first) */}
    <motion.div
      data-testid="hero-marker-plane"
      className="absolute z-10 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-sinovar-red shadow-lg shadow-sinovar-red/30"
      animate={{ left: ["51%", "31%", "50%"], top: ["29%", "50%", "72%"] }}
      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", times: [0, 0.5, 1], repeatDelay: 3.6 }}
    >
      <Plane className="h-4 w-4 text-white" strokeWidth={2} style={{ transform: "rotate(225deg)" }} />
    </motion.div>
  </div>
);

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, -30]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-white min-h-[600px] lg:min-h-[760px]">
      {/* full-bleed hero scene */}
      <motion.div style={{ y: yImg }} className="pointer-events-none absolute inset-0 hidden lg:block">
        <img src={ASSETS.heroScene} alt="China trade partner for Indonesian business" className="h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/80 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white to-transparent" />
      </motion.div>

      {/* animated trade map overlay (desktop) */}
      <div className="pointer-events-none absolute top-[9%] left-[31%] hidden w-[23%] lg:block z-[2]" style={{ aspectRatio: "505 / 543" }}>
        <TradeMap />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-8 pt-28 md:pt-32 lg:pt-40 pb-40 md:pb-44">
        <div className="lg:max-w-[50%]">
          <h1 className="font-display font-extrabold tracking-tight text-sinovar-navy text-[2.5rem] leading-[1.07] sm:text-5xl lg:text-6xl">
            <MaskText lines={[t.hero.l1]} mount />
            <span className="text-sinovar-red">
              <MaskText lines={[t.hero.l2]} lineClass="text-sinovar-red" start={0.14} mount />
              <MaskText lines={[t.hero.l3]} lineClass="text-sinovar-red" start={0.24} mount />
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-7 max-w-md text-lg text-sinovar-navy/70 leading-relaxed"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
          >
            <a
              href={waLink(t.hero.waMsg)}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-talk-cta"
              className="inline-flex items-center justify-center rounded-lg bg-sinovar-blue px-8 py-4 text-[15px] font-semibold text-white hover:bg-[#0b3c8a] transition-colors"
            >
              {t.hero.primary}
            </a>
            <Link
              to="/services"
              data-testid="hero-services-cta"
              className="inline-flex items-center justify-center rounded-lg border border-sinovar-blue/40 bg-white px-8 py-4 text-[15px] font-semibold text-sinovar-blue hover:bg-sinovar-blue/5 transition-colors"
            >
              {t.hero.secondary}
            </Link>
          </motion.div>
        </div>

        {/* mobile hero image with floating map overlay (unified like desktop) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="relative mt-10 overflow-hidden rounded-2xl lg:hidden"
        >
          <img src={ASSETS.heroScene} alt="China trade partner" className="w-full object-cover" />
          <div className="absolute left-3 top-[9%] w-[54%] rounded-xl border border-black/10 bg-white/75 p-1.5 shadow-lg backdrop-blur-sm" style={{ aspectRatio: "505 / 543" }}>
            <TradeMap />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
